import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  RevealableProperty,
  type Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { SnowBackground } from '@sorare/core/src/atoms/animations/SnowBackground';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { getClueAssets } from '#src/lib/crafting.ts';

import type { ClueCta_revealCost } from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  position: relative;
  &.disabled {
    opacity: 0.6;
  }
`;

const StyledBezel = styled(Bezel)`
  width: 100%;
  height: 100%;
`;
const InnerWrapper = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: var(--double-and-a-half-unit) var(--unit);

  word-break: break-word;
  gap: var(--half-unit);
  width: 100%;
  height: 100%;
  &:focus-visible {
    outline: none;
  }
  @media ${laptopAndAbove} {
    padding: var(--triple-unit) var(--quadruple-unit);
    flex-direction: row;
  }
`;

const Label = styled(Horizontal).attrs({
  gap: 0.5,
})`
  align-items: center;
  width: 100%;
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: var(--double-unit);
  height: var(--double-unit);
`;

const CluesAvailable = styled.div`
  position: absolute;
  right: calc(-1 * var(--unit));
  top: calc(-1 * var(--unit));
`;

const Icon = styled.img`
  display: flex;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ClueLabel = styled(BrandXS)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PaymentMethods = styled(Horizontal)`
  gap: 0;
  & > * + *:before {
    content: '/';
    color: var(--c-nd-500);
    margin: 0 var(--unit);
  }
`;

const SnowWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

type Props = {
  cluesAmount: Nullable<number>;
  onClick: () => void;
  used: boolean;
  disabled?: boolean;
  revealCost: ClueCta_revealCost;
  rarity: Rarity;
};

export const ClueCta = ({
  cluesAmount,
  onClick,
  used,
  disabled: disabledProp,
  revealCost,
  rarity,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const cluesAssets = getClueAssets(sport);
  const cardShardChest = currentUser?.cardShardsChests?.find(
    chest => chest.rarity === rarity
  );
  const { propertyName, amountInShards, amountInCurrency } = revealCost;
  if (!cluesAssets[propertyName]) {
    return null;
  }
  // user can buy clues with currencies or shards
  const hasEnoughCurrenciesToBuy = (cluesAmount ?? 0) >= amountInCurrency;
  const hasEnoughShardsToBuy =
    (cardShardChest?.cardShardsCount ?? 0) >= amountInShards;
  const disabled =
    disabledProp || (!hasEnoughCurrenciesToBuy && !hasEnoughShardsToBuy);

  return (
    <Wrapper
      className={classNames({
        disabled,
      })}
    >
      <StyledBezel
        bezelSize={16}
        radius={3}
        borderWidth={1}
        borderColor="var(--c-nd-100)"
        boxShadow="0 0 0 20px var(--c-nd-100)"
        background="conic-gradient(from 90deg at 2px 2px, var(--c-nd-50) 25%, var(--c-black) 0) 0 0/2px 2px"
      >
        <InnerWrapper onClick={onClick} disabled={disabled}>
          {!disabled && propertyName === RevealableProperty.RESHUFFLE && (
            <SnowWrapper>
              <SnowBackground />
            </SnowWrapper>
          )}
          <Label as="span" center>
            <IconWrapper>
              <Icon
                src={cluesAssets[propertyName]?.iconUrl}
                alt={propertyName}
              />
            </IconWrapper>
            <ClueLabel>
              <FormattedMessage {...cluesAssets[propertyName]?.shortLabel} />
            </ClueLabel>
          </Label>
        </InnerWrapper>
      </StyledBezel>
      <CluesAvailable>
        {!!used && (
          <Chip size="small" color="transparent">
            <BrandXS>
              <FormattedMessage defaultMessage="Used" />
            </BrandXS>
          </Chip>
        )}

        {!!disabled && !used && (
          <Chip size="small" color="transparent">
            <BrandXS>
              <FormattedMessage defaultMessage="Not available" />
            </BrandXS>
          </Chip>
        )}

        {!disabled && (
          <Chip
            size="small"
            color={hasEnoughCurrenciesToBuy ? 'brand' : 'transparent'}
          >
            <PaymentMethods>
              {!!hasEnoughCurrenciesToBuy && (
                <Horizontal as="span" gap={0.5}>
                  {amountInCurrency}
                  <img
                    src={cluesAssets[propertyName]?.iconUrl}
                    alt=""
                    height={16}
                  />
                </Horizontal>
              )}

              <BrandXS>
                {amountInShards}
                <Shards />
              </BrandXS>
            </PaymentMethods>
          </Chip>
        )}
      </CluesAvailable>
    </Wrapper>
  );
};

ClueCta.fragments = {
  revealCost: gql`
    fragment ClueCta_revealCost on RevealCost {
      propertyName
      amountInCurrency
      amountInShards
    }
  ` as TypedDocumentNode<ClueCta_revealCost>,
};
